import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { publicApi, fileUrl } from "../lib/apiClient";
import { Loader2, Sofa, ArrowRight, Heart, Sparkles, Palette } from "lucide-react";

export default function StyleBoardShare() {
  const { shareId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  const load = useCallback(() => {
    publicApi.get(`/public/style-board/${shareId}`).then((r) => setData(r.data)).catch(() => setErr(true));
  }, [shareId]);

  useEffect(() => { load(); }, [load]);

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0D1117] text-[#8B949E]" data-testid="board-share-error">
        This style board link is no longer available.
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0D1117]">
        <Loader2 className="w-8 h-8 animate-spin text-[#F59E0B]" />
      </div>
    );
  }

  const accent = "#F59E0B";
  const picks = data.picks || [];
  const terms = data.terms || [];

  return (
    <div className="min-h-screen bg-[#0D1117] text-[#F0F6FC]" data-testid="board-share-page">
      <header className="border-b border-[#21262D] px-6 py-4 flex items-center gap-2">
        <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: `${accent}22`, color: accent }}>
          <Sofa className="w-4 h-4" />
        </div>
        <span className="font-heading font-bold tracking-tight">ArchiMesh<span style={{ color: accent }}>AI</span></span>
        <span className="ml-auto text-xs uppercase tracking-widest text-[#6E7681]">Style Direction</span>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-center gap-2 mb-1">
          <Heart className="w-5 h-5" style={{ color: accent }} />
          <span className="text-xs uppercase tracking-widest text-[#8B949E]">Proposed direction</span>
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-2" data-testid="board-share-title">{data.title || "My Style Direction"}</h1>
        <p className="text-[#8B949E] text-sm mb-8 max-w-2xl">
          Here's the look and feel we're proposing before we produce the full designs. Take a look at the pieces and the material/colour palette below, then let us know if this feels right.
        </p>

        {terms.length > 0 && (
          <section className="mb-10" data-testid="board-share-palette">
            <div className="flex items-center gap-2 mb-3">
              <Palette className="w-4 h-4 text-[#38BDF8]" />
              <h2 className="font-heading font-semibold text-lg">Materials &amp; palette</h2>
            </div>
            <div className="flex flex-wrap gap-2">
              {terms.map((t, i) => (
                <span key={i} className="text-sm px-3 py-1.5 rounded-full bg-[#161B22] border border-[#30363D]" style={{ color: accent }}>{t}</span>
              ))}
            </div>
          </section>
        )}

        {picks.length > 0 && (
          <section data-testid="board-share-picks">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-4 h-4" style={{ color: accent }} />
              <h2 className="font-heading font-semibold text-lg">Reference designs ({picks.length})</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {picks.map((p, i) => (
                <figure key={i} className="rounded-2xl overflow-hidden border border-[#21262D] bg-[#0B0F14] flex flex-col" data-testid={`board-share-pick-${i}`}>
                  <div className="relative">
                    <img src={fileUrl(p.image)} alt={p.name || p.space} className="w-full h-56 object-cover" />
                    {p.bold && <span className="absolute top-2 left-2 text-[10px] px-1.5 py-0.5 rounded font-bold" style={{ background: accent, color: "#0D1117" }}>BOLD</span>}
                  </div>
                  <figcaption className="p-3">
                    <div className="font-heading font-semibold text-sm">{p.name || p.space}</div>
                    {p.palette && <div className="text-xs text-[#8B949E] mt-0.5"><span className="text-[#6E7681]">Palette:</span> {p.palette}</div>}
                    {p.materials && <div className="text-xs text-[#8B949E]"><span className="text-[#6E7681]">Materials:</span> {p.materials}</div>}
                  </figcaption>
                </figure>
              ))}
            </div>
          </section>
        )}

        {picks.length === 0 && terms.length === 0 && (
          <p className="text-[#6E7681]">This board is empty.</p>
        )}

        <a href="/" className="inline-flex items-center gap-2 mt-12 text-sm font-semibold hover:opacity-80 transition-opacity" style={{ color: accent }} data-testid="board-share-cta">
          Designed with ArchiMesh AI <ArrowRight className="w-4 h-4" />
        </a>
      </main>
    </div>
  );
}
